import React, { useMemo } from 'react';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { DragState } from '@/pages/datalab/components/SortableDrag/typeDefine';
import {
  ErrorDragType,
  ErrorDragMap,
  SortableAfter,
  SortableType,
} from '@dbl/core/chartEditor';
import { checkNullString, useDblIntl } from '@/pages/datalab/utils/utils';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { useIntl } from 'umi';
import BatchAddFieldPopover from '@/pages/datalab/pages/Multidimensional/Analysis/components/BatchAddFieldPopover';
import type { IBatchAddConfig } from '@/pages/datalab/pages/Multidimensional/Analysis/components/BatchAddFieldPopover/types';

export interface SortableAffixProps {
  item: IColumnInfo;
  dragState: DragState;
  sortableKey: string;
  dragSuffixText: string;
  disabledDrag?: boolean;
  disabledError?: string;
  batchAddConfig?: IBatchAddConfig;
}

export const SortableAffix = (props: SortableAffixProps) => {
  const { locale } = useIntl();
  const {
    item,
    dragState,
    sortableKey,
    dragSuffixText,
    disabledDrag,
    disabledError,
    batchAddConfig,
  } = props;
  const {
    errorDrag: { [sortableKey]: errorDragType },
  } = dragState;
  const { dblFormatMessage: dragFormatMessage } = useDblIntl('sortable_drag');
  /** 当前 Sortable 区域是否正处于「拖入目标」高亮态 */
  const isCurrentDragArea = dragState.currentDrag === sortableKey;
  /** 任意 Sortable 区域是否在拖拽中；传给 BatchAddFieldPopover 以强制关闭下拉 */
  const isAnyDragging = Boolean(dragState.currentDrag);

  const errorMsg = useMemo(() => {
    const textKey = locale === 'en-US' ? 'text_en' : 'text';
    let msg = '';
    if (checkNullString(errorDragType)) {
      msg = disabledError || '';
    } else if (errorDragType === ErrorDragType.Empty) {
      if (sortableKey === SortableType.Filter || sortableKey === SortableType.Compare) {
        msg = ErrorDragMap[errorDragType][textKey][0];
      } else if (sortableKey === SortableType.Metric) {
        msg = ErrorDragMap[errorDragType][textKey][1];
      } else if (sortableKey === SortableType.Dimension) {
        msg = ErrorDragMap[errorDragType][textKey][2];
      }
    } else {
      msg = ErrorDragMap[errorDragType][textKey] as string;
    }
    return msg;
  }, [errorDragType, sortableKey, locale, disabledError]);

  /**
   * 是否渲染可交互的 BatchAddFieldPopover（而非纯文案占位）。
   * - batchAddConfig.enabled：Sortable 侧开关（如图表类型/模式不支持则 false）
   * - !disabledDrag：图表字段数已满等场景禁用拖拽时也禁用批量入口
   * - !isCurrentDragArea：当前区域作为拖入目标时不展示下拉，避免与「释放鼠标」提示冲突
   */
  const showBatchAdd =
    batchAddConfig?.enabled &&
    !disabledDrag &&
    !isCurrentDragArea;

  return (
    <>
      {item.column_type === SortableAfter.Placeholder && (!disabledDrag) ? (
        <div
          key={`${item.id}_${item.column_type}`}
          className={`drag ${isCurrentDragArea ? 'drag-acitve' : 'dbl-sortable-container'}`}
        >
          {isCurrentDragArea ? (
            <span>{dragFormatMessage('release_mouse')}</span>
          ) : (
            <div className="dbl-sortable-affix-placeholder">
              {showBatchAdd ? (
                <BatchAddFieldPopover
                  config={batchAddConfig}
                  isDragging={isAnyDragging}
                  // dragSuffixText={dragSuffixText}
                />
              ) :
                <div className="dbl-sortable-affix-placeholder">
                  <span className="dbl-batch-add-popover__trigger">
                    <SvgIcon type={'cross'} />
                    {`${dragFormatMessage('batch_add_or_drag_drop')}${dragSuffixText}`}
                  </span>
                </div>
              }
            </div>
          )}
        </div>
      ) : ''
      }

      {
        item.column_type === SortableAfter.ErrorMessage && (!checkNullString(errorMsg)) ? (
          <div key={`${item.id}_${item.column_type}`} className={`dt-sortable-affix-group ${errorDragType}`} style={{ fontSize: '14px' }}>
            <SvgIcon className="erry-icon" type={errorDragType === 'empty' ? 'alert-yellow' : 'erry-tip-icon'} />
            <div className="erry-text">{errorMsg}</div>
          </div>
        ) : ''
      }

    </>
  );
};
